// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { ObservableOutput } from 'thehive-sdk';
import { Space, Collapse } from 'antd';
import { useLocalMessage } from '@Helpers/';
import AnalysisObservableCreation from '@Organisms/AnalysisData/AnalysisObservableCreation/AnalysisObservableCreation';
import { useParams, Link } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import THTable from '@Molecules/THTable/THTable';
import useListQuery from '@Hooks/useListQuery';
import { computeAlertListSelector, computeListSelector } from '@Organisms/Observable/selector';
import client from '@Client';
import { ColumnsType } from 'antd/lib/table';
import { ObservableEventList } from '@Organisms/Observable/eventList';
import Shortify from '@Atoms/Shortify/Shortify';
import { TableRowSelection } from 'antd/lib/table/interface';
import { formatObservableTitle } from '@Helpers/observable';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { ICONS } from '@Constants/iconsApp';
import { DisplayTags } from '@Atoms/Display';

import './AnalysisObservableTable.scss';

interface AnalysisObservableTableProps {
    data: ObservableOutput;
    observables: ObservableOutput[];
    analyzerName: string;
    eventList?: ObservableEventList;
    hasPermissions?: boolean;
    importObservable?: boolean;
}

const tokenizeObservableTitle = ({ data, attachment, dataType }: Partial<ObservableOutput>): string =>
    `${dataType}&${data || attachment?.name || ''}`;

export const AnalysisObservableTable = ({
    data,
    observables,
    analyzerName,
    eventList,
    importObservable,
}: AnalysisObservableTableProps): ReactElement => {
    const i = useLocalMessage();

    const { caseId, alertId } = useParams<{ caseId: string; alertId: string }>();
    const [observableKeys, setObservableKeys] = useState<React.ReactText[]>([]);
    const [alreadyExtractList, setAlreadyExtractList] = useState<Record<string, string>>({});
    const typeFilters = Array.from(new Set(observables.map(({ dataType }) => dataType))).map((dataType) => ({
        text: dataType,
        value: dataType,
    }));

    const { data: observableList = [], refetch: refetchObservableList } = useListQuery<ObservableOutput[]>({
        queryParams: {
            selector: caseId ? computeListSelector({ caseId }) : computeAlertListSelector({ alertId }),
        },
        controller: client.ObservableController,
    });

    const obsPath = useCallback(
        (observableId: string) =>
            caseId
                ? RouteBuilder.compute('cases.observable', { caseId, observableId })
                : RouteBuilder.compute('alerts.observable', { alertId, observableId }),
        [caseId, alertId],
    );

    const columns: ColumnsType<ObservableOutput> = useMemo(
        () => [
            {
                title: <ColumnTitle>{i('analyzer.observable.table.type')}</ColumnTitle>,
                filters: typeFilters,
                className: 'analysis-observable-table__type custom-table__cell',
                onFilter: (value, { dataType }): boolean => dataType.indexOf(value as string) === 0,
                render: (_, { dataType }) => dataType,
            },
            {
                title: <ColumnTitle>{i('analyzer.observable.table.value')}</ColumnTitle>,
                className: 'analysis-observable-table__value custom-table__cell',
                render: (_, { data, attachment, dataType, tags = [] }) => {
                    const _id = alreadyExtractList?.[tokenizeObservableTitle({ data, attachment, dataType })];

                    const line = <Shortify line={formatObservableTitle({ data, attachment })} maxSize={80} />;

                    return (
                        <div className="analysis-observable-table__name">
                            {_id ? (
                                <Link to={obsPath(_id)}>
                                    <Space size="small">
                                        {line}
                                        <ICONS.SearchIcon />
                                    </Space>
                                </Link>
                            ) : (
                                line
                            )}
                            <DisplayTags tags={tags} />
                        </div>
                    );
                },
            },
        ],
        [alreadyExtractList, obsPath, i, typeFilters],
    );

    const rowSelection: TableRowSelection<ObservableOutput> = useMemo(
        () => ({
            type: 'checkbox',
            onChange: (selectedKeys) => setObservableKeys(selectedKeys),
            selectedRowKeys: observableKeys,
            getCheckboxProps: ({ data, attachment, dataType }) => {
                const _id = alreadyExtractList?.[tokenizeObservableTitle({ data, attachment, dataType })];
                return {
                    disabled: !!_id,
                };
            },
        }),
        [alreadyExtractList, observableKeys],
    );

    useEffect(() => {
        const libExtracted = observableList.reduce((acc, { dataType, data, attachment, _id }) => {
            return {
                ...acc,
                [tokenizeObservableTitle({ data, attachment, dataType })]: _id,
            };
        }, {} as Record<string, string>);
        setAlreadyExtractList(libExtracted);
    }, [observableList]);

    return (
        <Collapse className="analysis-observable-table" defaultActiveKey={['1']}>
            <Collapse.Panel
                header={i('analyzer.observable.title')}
                className="analysis-observable-table__panel"
                key="1"
            >
                <THTable
                    title={() =>
                        importObservable && eventList ? (
                            <AnalysisObservableCreation
                                baseObservable={data}
                                observableKeys={observableKeys}
                                observables={observables}
                                setObservableKeys={setObservableKeys}
                                analyzerName={analyzerName}
                                onCreate={(values, isLast = false) =>
                                    caseId
                                        ? eventList.onCreate(
                                              values,
                                              caseId,
                                              isLast
                                                  ? undefined
                                                  : () => {
                                                        //empty
                                                    },
                                          )
                                        : eventList.onAlertCreate(
                                              values,
                                              alertId,
                                              isLast
                                                  ? undefined
                                                  : () => {
                                                        //empty
                                                    },
                                          )
                                }
                                onEnd={() => refetchObservableList()}
                            />
                        ) : null
                    }
                    size="small"
                    dataSource={observables}
                    rowKey="_id"
                    rowSelection={importObservable ? rowSelection : undefined}
                    columns={columns}
                />
            </Collapse.Panel>
        </Collapse>
    );
};
